import React, { useEffect, useRef, useState } from "react";
// eslint-disable-next-line no-restricted-imports -- TODO: update to `next/navigation` before using this component in App Router
import { useRouter } from "next/router";
import { stringify } from "qs";

import { Action, Category } from "@/constants/events";
import { Dispensary } from "@/custom-types/Dispensary";
import { MenuItem } from "@/custom-types/MenuItem";
import useDomainCountryCode from "@/hooks/useDomainCountryCode";
import { useEventTracker } from "@/hooks/useEventTracker";
import { useSelector } from "@/redux/hooks";
import { getUserId } from "@/redux/selectors/user";
import { getRoundedDistance } from "@/utils/distance";
import { localizedDistance } from "@/utils/localizedDistance";
import {
  scrollToSetPosition,
  trackScrollPosition,
} from "@/utils/scrollPosition";

import Button from "@/components/botanic/Button";
import MenuItemCarousel from "@/components/Shared/MenuItemCarousel";
import MenuItemCarouselPlaceholder from "@/components/Shared/MenuItemCarousel/MenuItemCarouselPlaceholder";

const ShopMenuItemCarousels: React.FC<{
  dispensaries: Dispensary[];
  loading?: boolean;
}> = ({ dispensaries, loading }) => {
  const {
    query: {
      q,
      shopCategory,
      shopName,
      product_category,
      strain_category,
      strain_name,
      sort,
      sort_direction,
      showSimilar,
    },
  } = useRouter();
  const countryCode = useDomainCountryCode();
  const { publishEvent } = useEventTracker();
  const userId = useSelector(getUserId);

  const [dispensariesVisible, setDispensariesVisible] = useState(6);
  const initialLoad = useRef(true);
  useEffect(() => {
    setDispensariesVisible(6);

    if (dispensaries.length && initialLoad.current) {
      scrollToSetPosition();
      trackScrollPosition(true);
      initialLoad.current = false;
    }
    return function cleanup() {
      trackScrollPosition(false);
    };
  }, [dispensaries.length]);

  const seeMoreClick = () => {
    publishEvent({
      action: Action.click,
      category: Category.loadMoreResults,
      label: "load more retailer menus",
    });

    setDispensariesVisible(dispensariesVisible + 6);
  };

  const trackHeaderLinkClick = React.useCallback(
    (dispensaryId: number, label: string, distanceMi?: number) => {
      publishEvent({
        action: Action.click,
        category: Category.menuItemCardTray,
        // @ts-ignore -- TODO: Fix publishEvent types (https://leafly.atlassian.net/browse/SHH-1133)
        dispensaryDistance: getRoundedDistance(distanceMi, countryCode),
        dispensaryId,
        label,
        userId: userId as number,
      });
    },
    [userId],
  );

  const trackMenuItemCardClick = React.useCallback(
    (menuItem: MenuItem, label = "") => {
      publishEvent({
        action: Action.click,
        brandId: menuItem.brand?.id as number,
        category: Category.menuItemCard,
        dispensaryId: menuItem.dispensary.id,
        label,
        menuItemId: menuItem.id,
        productId: menuItem.product?.id as number,
        strainId: menuItem.strain?.id as number,
        userId: userId as number,
      });
    },
    [userId],
  );

  const handleViewDetailsClick = React.useCallback((menuItem: MenuItem) => {
    trackMenuItemCardClick(menuItem, "view details button");
  }, []);

  const showSimilarStrains =
    shopCategory === "strain" &&
    (showSimilar === "on" || showSimilar === "menu_items_only");

  const sortConfig =
    sort && sort_direction
      ? { [String(sort)]: String(sort_direction) }
      : undefined;

  const filter = {
    brand_name:
      shopCategory === "brand" && shopName
        ? Array.isArray(shopName)
          ? shopName
          : [String(shopName)]
        : undefined,
    product_category: product_category
      ? Array.isArray(product_category)
        ? product_category
        : [String(product_category)]
      : undefined,
    strain_category: strain_category
      ? Array.isArray(strain_category)
        ? strain_category
        : [String(strain_category)]
      : undefined,
    strain_name:
      (strain_name
        ? Array.isArray(strain_name)
          ? strain_name
          : [String(strain_name)]
        : undefined) ||
      (shopCategory === "strain" && shopName
        ? Array.isArray(shopName)
          ? shopName
          : [String(shopName)]
        : undefined),
  };

  const showSeeMore = dispensaries.length > dispensariesVisible;

  return (
    <div className="container">
      {loading ? (
        <MenuItemCarouselPlaceholder />
      ) : (
        dispensaries
          .slice(0, dispensariesVisible)
          .map(
            ({
              city,
              deliveryEnabled,
              distanceMi,
              id,
              name,
              pickupEnabled,
              slug,
              state,
            }) => {
              const distance = localizedDistance(distanceMi, countryCode);
              const showDistance = !deliveryEnabled || pickupEnabled;
              const showOnlineOrdering = deliveryEnabled || pickupEnabled;
              const cityDisplay = city ? `${city}, ` : "";
              const subHeader =
                distance && showDistance
                  ? `${distance} away - ${cityDisplay}${state}`
                  : `${cityDisplay}${state}`;

              const dispensaryLink = `/dispensary-info/${slug}/menu?${stringify(
                {
                  q,
                  ...filter,
                },
                { arrayFormat: "brackets", encodeValuesOnly: true },
              )}`;

              return (
                <MenuItemCarousel
                  actionHref={dispensaryLink}
                  actionOnClick={() =>
                    trackHeaderLinkClick(
                      id,
                      "show all matching products link",
                      distanceMi,
                    )
                  }
                  actionText={
                    showSimilarStrains
                      ? "Show matching products"
                      : "Show %tc matching products"
                  }
                  cardOnClick={trackMenuItemCardClick}
                  className="mb-xxl"
                  filter={filter}
                  header={name}
                  headerLink={dispensaryLink}
                  headerOnClick={() =>
                    trackHeaderLinkClick(id, "retailer header link", distanceMi)
                  }
                  key={name}
                  query={q ? String(q) : undefined}
                  showOnlineOrdering={showOnlineOrdering}
                  slug={slug}
                  sort={sortConfig ? { 0: sortConfig } : undefined}
                  subheader={subHeader}
                  viewDetailsOnClick={handleViewDetailsClick}
                />
              );
            },
          )
      )}

      {showSeeMore && (
        <Button
          data-testid="load-more-results"
          variant="secondary"
          onClick={seeMoreClick}
          className="-mt-xl"
        >
          load more results
        </Button>
      )}
    </div>
  );
};

export default ShopMenuItemCarousels;
